import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import Footer from "../../layout/Footer";
import axios from "axios";
import { ThreeDots } from "react-loader-spinner";

function Dashboard() {
  // State for dashboard counts
  const [dashboardData, setDashboardData] = useState({
    status: false,
    data: {
      users: 0,
      houses: 0,
      visits: 0,
      templates: 0,
      managers: 0,
      flags: 0,
      editors: 0,
      companies: 0,
      activeOnly: true
    }
  });

  // State for active filter toggle
  const [activeOnly, setActiveOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    var element = document.getElementById("page-container");
    element.classList.add("show");
    getDashboardCounts();
  }, [activeOnly]);

  const getDashboardCounts = useCallback(async () => {
    setLoading(true);
    setError(null);

    const myurl = `${process.env.REACT_APP_base_url}api/dashboard/get-dashboard-counts`;

    const requestBody = {
      auth_code: process.env.REACT_APP_AUTH_CODE,
      activeOnly: activeOnly,
    };

    try {
      const response = await axios.post(myurl, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success === true) {
        setDashboardData({
          status: true,
          data: response.data.data,
        });
      } else {
        setError(response.data.message || "Failed to fetch dashboard data");
      }
    } catch (err) {
      setError("Network error occurred while fetching dashboard data");
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  const handleToggleFilter = () => {
    setActiveOnly(!activeOnly);
  };

  const handleRefresh = () => {
    getDashboardCounts();
  };

  // Stat card component for reusability
  const StatCard = ({
    bgColor,
    icon,
    title,
    count,
    dataType,
    isLoading
  }) => (
    <div className="col-xl-3 col-md-6">
      <div className={`widget widget-stats ${bgColor}`}>
        <div className="stats-icon">
          <i className={icon}></i>
        </div>
        <div className="stats-info">
          <h4>{title}</h4>
          <p>
            {isLoading ? (
              <ThreeDots
                height="35"
                width="35"
                radius="9"
                color="rgba(255, 255, 255, 0.8)"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClassName=""
                visible={true}
              />
            ) : (
              count.toLocaleString()
            )}
          </p>
        </div>
        <div className="stats-link">
          <Link
            to="/detailed-dashboard"
            state={{
              title: title,
              dataType: dataType,
              activeOnly: activeOnly,
            }}
          >
            View Detail <i className="fa fa-arrow-alt-circle-right"></i>
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Loader />
      <div
        id="page-container"
        className="fade page-sidebar-fixed page-header-fixed"
      >
        <Menu />
        <div id="content" className="content">
          {/* Breadcrumb */}
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <a href="#!">Home</a>
            </li>
            <li className="breadcrumb-item active">Dashboard</li>
          </ol>

          {/* Header */}
          <h1 className="page-header">Dashboard</h1>

          {/* Summary Card - Moved to top */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center" style={{ gap: "16px" }}>
                      {/* Active Filter Toggle */}
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="activeFilterToggle"
                          checked={activeOnly}
                          onChange={handleToggleFilter}
                          style={{ transform: 'scale(1.3)' }}
                          disabled={loading}
                        />
                        <label className="form-check-label fw-bold fs-5" htmlFor="activeFilterToggle" style={{ paddingLeft: "16px" }}>
                          {activeOnly ? 'Active Companies' : 'All Companies'}
                        </label>
                      </div>

                      {/* Status Badge */}
                      <span className={`badge ${activeOnly ? 'bg-success' : 'bg-secondary'} fs-6 px-3 py-2`}>
                        <i className={`fa ${activeOnly ? 'fa-check-circle' : 'fa-list'} me-2`} style={{ paddingRight: "4px" }}></i>
                        {activeOnly ? 'Active Filter ON' : 'Showing All Data'}
                      </span>
                    </div>

                    {/* Refresh Button */}
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleRefresh}
                      disabled={loading}
                      title="Refresh Dashboard"
                    >
                      <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''} me-2`}></i>
                      {loading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="alert alert-danger alert-dismissible" role="alert">
                  <button type="button" className="btn-close" onClick={() => setError(null)}></button>
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  {error}
                  <button
                    className="btn btn-outline-danger btn-sm ms-3"
                    onClick={handleRefresh}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard Stats Cards */}
          <div className="row">

            {/* Companies Card (Special case - with company detail view) */}
            <div className="col-xl-3 col-md-6">
              <div
                className="widget widget-stats"
                style={{ backgroundColor: "#34D565" }}
              >
                <div className="stats-icon">
                  <i className="fa fa-building"></i>
                </div>
                <div className="stats-info">
                  <h4>Total Companies</h4>
                  <p>
                    {loading ? (
                      <ThreeDots
                        height="35"
                        width="35"
                        radius="9"
                        color="rgba(255, 255, 255, 0.8)"
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible={true}
                      />
                    ) : (
                      dashboardData.data.companies.toLocaleString()
                    )}
                  </p>
                </div>
                <div className="stats-link">
                  <Link
                    to="/company"
                    state={{
                      activeOnly: activeOnly,
                    }}
                  >
                    View Detail <i className="fa fa-arrow-alt-circle-right"></i>
                  </Link>
                </div>
              </div>
            </div>

            <StatCard
              bgColor="bg-info"
              icon="fa fa-users"
              title="Total Users"
              count={dashboardData.data.users}
              dataType="users"
              isLoading={loading}
            />

            <StatCard
              bgColor="bg-purple"
              icon="fa fa-home"
              title="Total Houses"
              count={dashboardData.data.houses}
              dataType="houses"
              isLoading={loading}
            />

            <StatCard
              bgColor="bg-blue"
              icon="fa fa-handshake"
              title="Total Visits"
              count={dashboardData.data.visits}
              dataType="visits"
              isLoading={loading}
            />

            <StatCard
              bgColor="bg-orange"
              icon="fa fa-list"
              title="Total Templates"
              count={dashboardData.data.templates}
              dataType="templates"
              isLoading={loading}
            />

            <StatCard
              bgColor="bg-pink"
              icon="fa fa-calendar-check"
              title="Total Managers"
              count={dashboardData.data.managers}
              dataType="managers"
              isLoading={loading}
            />

            <StatCard
              bgColor="bg-gray"
              icon="fa fa-flag"
              title="Total Flags"
              count={dashboardData.data.flags}
              dataType="flags"
              isLoading={loading}
            />

            <StatCard
              bgColor="bg-yellow"
              icon="fa fa-edit"
              title="Total Editors"
              count={dashboardData.data.editors}
              dataType="editors"
              isLoading={loading}
            />
          </div>

        </div>
        <Footer />
      </div>
    </>
  );
}

export default Dashboard;